import type {
  ICalculatorDefaults,
  ICalculatorVisitorPreferences,
  IFinanceRateInfo,
  IPrequalResult,
  ITaxesAndFees,
  PersonalFinanceEnvironment,
} from '../types';

const fallbackCreditRates: IFinanceRateInfo[] = [
  {
    type: 'Excellent',
    minimumRate: 5.24,
    maximumRate: 8.49,
    medianRate: 5.24,
    minimumFicoScore: 800,
    maximumFicoScore: null,
  },
  {
    type: 'VeryGood',
    minimumRate: 7.24,
    maximumRate: 11.35,
    medianRate: 8.35,
    minimumFicoScore: 740,
    maximumFicoScore: 799,
  },
  {
    type: 'Good',
    minimumRate: 11.24,
    maximumRate: 15.25,
    medianRate: 12.25,
    minimumFicoScore: 670,
    maximumFicoScore: 739,
  },
  {
    type: 'Fair',
    minimumRate: 15.24,
    maximumRate: 19.5,
    medianRate: 16.5,
    minimumFicoScore: 580,
    maximumFicoScore: 669,
  },
  {
    type: 'Challenged',
    minimumRate: 21.24,
    maximumRate: 24.5,
    medianRate: 21.5,
    minimumFicoScore: null,
    maximumFicoScore: 579,
  },
];

export function getBaseUrl(environment: PersonalFinanceEnvironment): string {
  return environment === 'Prod'
    ? 'https://www.carmax.com'
    : 'https://wwwqa.carmax.com';
}

export async function getCreditRates(
  environment: PersonalFinanceEnvironment,
): Promise<IFinanceRateInfo[]> {
  try {
    const response = await fetch(
      `${getBaseUrl(environment)}/pre-qual-gateway/api/rates`,
    );
    if (!response.ok) {
      console.error('Failed to fetch finance rates:', response.status);
      return fallbackCreditRates;
    }
    const data = (await response.json()) as IFinanceRateInfo[] | null;
    return data ?? fallbackCreditRates;
  } catch (error) {
    console.error('Error fetching finance rates:', error);
    return fallbackCreditRates;
  }
}

export async function getTaxesAndFees(
  environment: PersonalFinanceEnvironment,
  storeId: number,
): Promise<ITaxesAndFees> {
  try {
    const response = await fetch(
      `${getBaseUrl(environment)}/pre-qual-gateway/api/stores/${storeId}/taxesandfees`,
    );
    if (!response.ok) {
      console.error(`Fee schedule fetch failed: ${response.status}`);
      throw new Error(`HTTP ${response.status}`);
    }
    const data = (await response.json()) as ITaxesAndFees;
    if (!data?.taxesAndFees || !Array.isArray(data.taxesAndFees)) {
      console.error('Invalid fee schedule structure:', data);
      throw new Error('Invalid fee schedule structure');
    }
    return {
      taxesAndFees: data.taxesAndFees,
      stateAbbreviation: data.stateAbbreviation,
    };
  } catch (error) {
    console.error('Error in getFeeSchedule:', {
      message: error instanceof Error ? error.message : 'Unknown error',
      storeId,
    });
    throw new Error(
      `Failed to fetch fee schedule: ${error instanceof Error ? error.message : 'Unknown error'}`,
    );
  }
}

export async function getFullPrequalResult(
  environment: PersonalFinanceEnvironment,
): Promise<IPrequalResult | null> {
  const response = await fetch(
    `${getBaseUrl(environment)}/pre-qual-gateway/api/v1/pfc/prequal-results`,
    { credentials: 'include' },
  );
  if (response.status === 408 || response.status >= 500) {
    throw new Error(
      `Failed to fetch prequalification: HTTP ${response.status}`,
    );
  }
  if (response.status >= 400) {
    return null;
  }
  return ((await response.json()) as IPrequalResult | null) ?? null;
}

export async function getEligibleCarCount(
  environment: PersonalFinanceEnvironment,
): Promise<number | null> {
  try {
    const response = await fetch(
      `${getBaseUrl(environment)}/pre-qual-gateway/api/totalcarcount`,
    );
    if (!response.ok) {
      return null;
    }
    const count = (await response.json()) as unknown;
    return typeof count === 'number' && count > 0 ? count : null;
  } catch (error) {
    console.error('Error fetching eligible car count:', error);
    return null;
  }
}

export async function getCalculatorDefaults(
  environment: PersonalFinanceEnvironment,
  visitorId: string,
): Promise<ICalculatorDefaults> {
  const params = new URLSearchParams({ visitorid: visitorId });
  const response = await fetch(
    `${getBaseUrl(environment)}/pre-qual-gateway/api/calculator/defaults?${params}`,
  );
  if (!response.ok) {
    throw new Error(
      `Failed to fetch calculator defaults: HTTP ${response.status}`,
    );
  }
  return (await response.json()) as ICalculatorDefaults;
}

export async function getVisitorCalculatorPreferences(
  environment: PersonalFinanceEnvironment,
  visitorId: string,
): Promise<ICalculatorVisitorPreferences> {
  const params = new URLSearchParams({ visitorid: visitorId });
  const response = await fetch(
    `${getBaseUrl(environment)}/pre-qual-gateway/api/calculator/preferences?${params}`,
  );
  if (!response.ok) {
    throw new Error(
      `failed to fetch Calculator Visitor Preferences. response.status: ${response.status}`,
    );
  }
  return (await response.json()) as ICalculatorVisitorPreferences;
}

export async function putVisitorCalculatorPreferences(
  environment: PersonalFinanceEnvironment,
  preferences: ICalculatorVisitorPreferences,
): Promise<void> {
  const response = await fetch(
    `${getBaseUrl(environment)}/pre-qual-gateway/api/calculator/preferences`,
    {
      method: 'PUT',
      headers: new Headers([
        ['Content-Type', 'application/json'],
        ['Accept', 'application/json'],
      ]),
      body: JSON.stringify(preferences),
    },
  );
  if (!response.ok) {
    throw new Error(
      `failed to save visitor preferences. response.status: ${response.status}`,
    );
  }
}
